import { useEffect, useState } from "react";
import { isOpenableUrl } from "@/lib/open-document-tab";
import { resolveStoredFileUrl, storageRefFor } from "@/lib/storage-url";

export type SignedStoredUrl = {
  /** The signed URL, or null while signing and after a failure. */
  url: string | null;
  /** True once signing failed: the caller shows that instead of a blank. */
  failed: boolean;
};

/**
 * A value that isn't ours to sign (an external http(s) or blob: URL) is used
 * as is. Any other scheme goes through resolveStoredFileUrl, which refuses it:
 * a stored `javascript:` must never become an <img>/<iframe> src.
 */
function passThrough(
  value: string | null | undefined,
  bucket: string
): string | null {
  return value && isOpenableUrl(value) && !storageRefFor(value, bucket)
    ? value
    : null;
}

/**
 * A signed URL for a stored file that is rendered inline (an `<img>`, an
 * `<iframe>`) rather than opened on click, so the stored public URL never
 * reaches the DOM (COL-436). Re-signs when the value changes. The default
 * expiry is an hour: the element stays on screen for as long as the page.
 */
export function useSignedStoredUrl(
  value: string | null | undefined,
  defaultBucket: string,
  expiresIn = 3600
): SignedStoredUrl {
  const [state, setState] = useState<SignedStoredUrl>(() => ({
    url: passThrough(value, defaultBucket),
    failed: false,
  }));
  useEffect(() => {
    const direct = passThrough(value, defaultBucket);
    setState({ url: direct, failed: false });
    if (!value || direct) return;
    let live = true;
    resolveStoredFileUrl(value, defaultBucket, { expiresIn }).then(
      (url) => {
        if (live) setState({ url, failed: !url });
      },
      () => {
        if (live) setState({ url: null, failed: true });
      }
    );
    return () => {
      live = false;
    };
  }, [value, defaultBucket, expiresIn]);
  return state;
}
